import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { SkeletonRow } from "@d12labs/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { KanbanSquare } from "lucide-react";
// Deal derives from the server's route defs (Phase 7) via ../api → ../types.
import { crmApi } from "../api";
import { DEAL_STATUSES, type Deal, type DealStatus, formatMoney } from "../types";

const STATUS_LABEL: Record<DealStatus, string> = {
	open: "Open",
	won: "Won",
	lost: "Lost",
};

const STATUS_TONE: Record<DealStatus, "subtle" | "success" | "danger"> = {
	open: "subtle",
	won: "success",
	lost: "danger",
};

const STATUS_DOT: Record<DealStatus, string> = {
	open: "var(--color-ink-tertiary)",
	won: "var(--color-success)",
	lost: "var(--color-danger)",
};

export function PipelinePage() {
	const { data: deals, isLoading } = useQuery({
		queryKey: ["crm", "deals"],
		queryFn: () => crmApi.listDeals(),
	});

	const grouped = DEAL_STATUSES.reduce(
		(acc, status) => {
			acc[status] = (deals ?? []).filter((d) => d.status === status);
			return acc;
		},
		{} as Record<DealStatus, Deal[]>,
	);

	const totalValue = (deals ?? []).reduce((s, d) => s + d.valueCents, 0);
	const totalCount = deals?.length ?? 0;

	return (
		<>
			<PageHeader
				icon={KanbanSquare}
				title="Pipeline"
				subtitle="Track deals across stages."
				badges={
					totalCount > 0 ? (
						<>
							<Badge tone="subtle">{totalCount} deals</Badge>
							<Badge tone="subtle">{formatMoney(totalValue)}</Badge>
						</>
					) : undefined
				}
				actions={
					<Link to="/crm">
						<Button variant="ghost">Contacts</Button>
					</Link>
				}
			/>

			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					{isLoading ? (
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							{DEAL_STATUSES.map((status) => (
								<div
									key={status}
									className="rounded-lg border border-hairline bg-surface-1 overflow-hidden"
								>
									<div className="flex items-center justify-between border-b border-hairline px-4 h-10">
										<span className="text-body-sm font-medium text-foreground">
											{STATUS_LABEL[status]}
										</span>
									</div>
									<div className="p-2 space-y-1">
										<SkeletonRow columns={[{ width: "1fr" }, { width: "80px" }]} />
										<SkeletonRow columns={[{ width: "1fr" }, { width: "80px" }]} />
									</div>
								</div>
							))}
						</div>
					) : totalCount === 0 ? (
						<EmptyState
							icon={KanbanSquare}
							title="No deals yet"
							body="Go to a contact page and add a deal to start tracking your pipeline."
							primary={
								<Link to="/crm">
									<Button variant="secondary">View contacts</Button>
								</Link>
							}
						/>
					) : (
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							{DEAL_STATUSES.map((status) => {
								const items = grouped[status];
								const colTotal = items.reduce((s, d) => s + d.valueCents, 0);
								return (
									<div
										key={status}
										className="rounded-lg border border-hairline bg-surface-1 overflow-hidden"
									>
										<div className="flex items-center justify-between border-b border-hairline px-4 h-10">
											<span className="flex items-center gap-1.5 text-body-sm font-medium text-foreground">
												<span
													className="size-2 rounded-full"
													style={{ background: STATUS_DOT[status] }}
												/>
												{STATUS_LABEL[status]}
											</span>
											<span className="text-meta text-ink-subtle">
												{items.length} · {formatMoney(colTotal)}
											</span>
										</div>
										{items.length === 0 ? (
											<div className="px-4 py-6">
												<p className="text-body-sm text-ink-subtle text-center">No deals.</p>
											</div>
										) : (
											<ul className="flex flex-col p-2 gap-1.5">
												{items.map((d) => (
													<li key={d.id}>
														<Link
															to="/crm/contacts/$id"
															params={{ id: d.contactId }}
															className="flex items-center justify-between rounded-md border border-hairline bg-canvas px-3 py-2 text-body-sm hover:bg-surface-1 hover:border-hairline-strong transition-colors"
														>
															<div className="min-w-0">
																<div className="font-medium text-foreground truncate">
																	{d.title}
																</div>
																<div className="text-meta text-ink-subtle">
																	{formatMoney(d.valueCents)}
																</div>
															</div>
															{/* Items are filtered to this column's status; the derived row's
															    status is plain string, so index with the column key. */}
															<Badge tone={STATUS_TONE[status]} dot={STATUS_DOT[status]}>
																{STATUS_LABEL[status]}
															</Badge>
														</Link>
													</li>
												))}
											</ul>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</PageBody>
		</>
	);
}
